import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Box,
  InputAdornment,
  FormHelperText,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Switch,
} from '@mui/material';
import {
  Person,
  LocationOn,
  Assignment,
  Close,
  Save,
  Search,
  Add,
  Email,
  Phone,
  ContactPhone,
  Delete,
  Notifications,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { Client } from '../../types';
import { clientService } from '../../services/clientService';
import { contactService } from '../../services/contactService';
import EstadosBrSelect from '../selects/EstadosBrSelect';
import BairroSelect from '../selects/BairroSelect';
import ClientSelect from '../selects/ClientSelect';
import EscritorioSelect from '../selects/EscritorioSelect';
import FuncionariosSelect from '../selects/FuncionariosSelect';
import { useAuth } from '../../contexts/AuthContext';
import { formatarCPFCNPJ, formatarCPFCNPJOnInput, formatarCepOnInput, buscarCep, formatarPhone } from '../../utils/formatToView';
import ContactForm from './ContactForm';

// Opções em ordem alfabética para "Como conheceu a Empresa?"
const COMO_CONHECEU_OPTIONS = [
  'Carro da empresa',
  'Facebook',
  'Google',
  'Indicação de amigo',
  'Instagram',
  'Outdoor',
  'Outro',
  'Panfleto',
  'Passou em frente à loja',
  'Rádio',
  'Site',
  'TikTok',
  'Whatsapp',
];

interface ClientFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (client: Client) => void;
  editingClient?: Client | null;
}

/** Contato pendente (antes do cliente ser criado) */
interface PendingContact {
  nome?: string;
  tempId: string;
  tipo: 'email' | 'fixo' | 'celular';
  email?: string;
  numero?: string;
  is_whatsapp: boolean;
  is_default: boolean;
  recebe_notificacao: boolean;
}

// Schema de validação para cliente
const clientSchema = yup.object().shape({
  nome: yup
    .string()
    .required('Nome é obrigatório')
    .min(2, 'Nome deve ter pelo menos 2 caracteres')
    .max(100, 'Nome deve ter no máximo 100 caracteres')
    .test(
      'no-trailing-space',
      'Remova o(s) espaço(s) no final do nome',
      (value) => !value || value === value.trimEnd()
    ),
  cpf_cnpj: yup
    .string()
    .required('CPF/CNPJ é obrigatório')
    .min(11, 'CPF deve ter 11 dígitos ou CNPJ 14 dígitos')
    .max(18, 'Formato inválido'),
  tipo_pessoa: yup
    .string()
    .required('Tipo de cliente é obrigatório')
    .oneOf(['pf', 'pj'], 'Tipo inválido'),
  tipo_cliente: yup
    .string()
    .required('Tipo de cliente é obrigatório')
    .oneOf(['01', '02', '03', '04', '05', '06', '07', '08', '99'], 'Tipo de cliente inválido'),
  data_nascimento: yup
    .date()
    .nullable()
    .when('tipo_pessoa', {
      is: 'pf',
      then: (schema) => schema.required('Data de nascimento é obrigatória para pessoa física'),
      otherwise: (schema) => schema.nullable(),
    }),
  rg_ie: yup
    .string()
    .nullable()
    .test('rg-ie-validation', 'Validação de documento falhou', function (value) {
      const { tipo_pessoa, contribuinte_icms } = this.parent;
      const numContribuinte = Number(contribuinte_icms);

      // Se for Contribuinte Isento (2), deve ser ISENTO
      if (numContribuinte === 2) {
        if (value?.toUpperCase() !== 'ISENTO') {
          return this.createError({ message: 'Deve ser ISENTO para contribuinte isento' });
        }
        return true;
      }

      // Se for Contribuinte (1), é obrigatório e não pode ser ISENTO
      if (numContribuinte === 1) {
        if (!value || value.trim() === '') {
          return this.createError({ message: 'Inscrição Estadual é obrigatória para contribuinte' });
        }
        if (value.toUpperCase() === 'ISENTO') {
          return this.createError({ message: 'Não pode ser ISENTO para contribuinte' });
        }
      }

      // Validação para PJ (Independente do ICMS, se já passou pelas regras acima)
      if (tipo_pessoa === 'pj') {
        if (!value || value.trim() === '') {
          return this.createError({ message: 'Inscrição Estadual (IE) é obrigatória para pessoa jurídica' });
        }
        if (!/^\d+$/.test(value) && value.toUpperCase() !== 'ISENTO') {
          return this.createError({ message: 'IE deve conter apenas números ou ser ISENTO' });
        }
        if (value.length < 2 || value.length > 20) {
          return this.createError({ message: 'IE deve ter entre 2 e 20 caracteres' });
        }
      }

      // Validação para PF (RG opcional, mas se preenchido valida tamanho)
      if (tipo_pessoa === 'pf' && value && value.trim() !== '') {
        if (value.length < 6 || value.length > 11) {
          return this.createError({ message: 'RG deve ter entre 6 e 11 caracteres' });
        }
      }

      return true;
    }),

  contribuinte_icms: yup
    .number()
    .required('Contribuinte ICMS é obrigatório'),
  endereco: yup
    .string()
    .required('Endereço é obrigatório')
    .min(5, 'Endereço deve ter pelo menos 5 caracteres'),
  numero: yup
    .string()
    .required('Número é obrigatório')
    .min(1, 'Número deve ter pelo menos 1 caractere'),
  bairro: yup
    .string()
    .required('Bairro é obrigatório')
    .min(2, 'Bairro deve ter pelo menos 2 caracteres'),
  cidade: yup
    .string()
    .required('Cidade é obrigatória')
    .min(2, 'Cidade deve ter pelo menos 2 caracteres'),
  estado: yup
    .string()
    .required('Estado é obrigatório')
    .length(2, 'Estado deve ter 2 caracteres (UF)'),
  cep: yup
    .string()
    .required('CEP é obrigatório')
    .matches(/^\d{5}-?\d{3}$/, 'CEP deve ter formato 00000-000'),
  observacoes: yup
    .string()
    .nullable()
    .max(1000, 'Observações devem ter no máximo 1000 caracteres'),
  como_conheceu: yup
    .string()
    .required('Como conheceu a Empresa é obrigatório'),
  indicado_por: yup
    .number()
    .nullable()
    .integer('ID do cliente deve ser um número inteiro')
    .positive('ID do cliente deve ser positivo')
    .when('como_conheceu', {
      is: 'Indicação de amigo',
      then: (schema) => schema.required('Selecione o cliente que fez a indicação'),
      otherwise: (schema) => schema.nullable(),
    }),
  vendedor_id: yup
    .number()
    .nullable()
    .integer('ID do funcionário deve ser um número inteiro')
    .positive('ID do funcionário deve ser positivo'),
  escritorio_id: yup
    .number()
    .required('Escritório é obrigatório')
    .integer('ID do escritório deve ser um número inteiro')
    .positive('ID do escritório deve ser positivo'),
  tipo_moradia: yup
    .string()
    .nullable()
    .oneOf(['propria', 'alugado'], 'Tipo de moradia inválido'),
  coordenadas: yup
    .string()
    .nullable(),
});

const ClientForm: React.FC<ClientFormProps> = ({
  open,
  onClose,
  onSuccess,
  editingClient,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cepLoading, setCepLoading] = useState(false);
  const [cpfCnpjChecking, setCpfCnpjChecking] = useState(false);
  const [contacts, setContacts] = useState<PendingContact[]>([]);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  // Estados para modal do mapa
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number, lng: number } | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<Partial<Client>>({
    resolver: yupResolver(clientSchema) as any,
    defaultValues: {
      nome: '',
      cpf_cnpj: '',
      tipo_pessoa: 'pf',
      data_nascimento: undefined,
      rg_ie: '',
      contribuinte_icms: 0,
      nome_mae: '',
      nome_pai: '',
      endereco: '',
      numero: '',
      bairro: '',
      cidade: '',
      estado: '',
      codigo_municipio: '',
      cep: '',
      complemento: '',
      referencia: '',
      observacoes: '',
      tipo_cliente: '',
      tipo_moradia: undefined as 'propria' | 'alugado' | undefined,
      indicado_por: undefined,
      vendedor_id: undefined,
      escritorio_id: undefined, // Será preenchido automaticamente
    },
  });

  const scrollToTop = () => {
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Scroll ao topo quando houver erro manual
  useEffect(() => {
    if (error) {
      scrollToTop();
    }
  }, [error]);

  // Scroll ao topo quando houver erro de validação do formulário
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      scrollToTop();
    }
  }, [errors]);

  const tipoClienteWatch = watch('tipo_pessoa');
  const nomeWatch = watch('nome');
  const cepWatch = watch('cep');
  const contribuinteIcmsWatch = watch('contribuinte_icms');
  const cidadeWatch = watch('cidade');
  const estadoWatch = watch('estado');
  const comoConheceuWatch = watch('como_conheceu');

  // Limpar indicado_por quando como_conheceu for diferente de 'Indicação de amigo'
  useEffect(() => {
    if (comoConheceuWatch && comoConheceuWatch !== 'Indicação de amigo') {
      setValue('indicado_por', undefined, { shouldValidate: true });
    }
  }, [comoConheceuWatch, setValue]);

  const hasTrailingSpaceInNome =
    typeof nomeWatch === 'string' && nomeWatch.length > 0 && nomeWatch !== nomeWatch.trimEnd();

  // Lógica para Contribuinte ICMS Isento (2)
  useEffect(() => {
    const numContribuinte = Number(contribuinteIcmsWatch);
    if (numContribuinte === 2) {
      setValue('rg_ie', 'ISENTO');
    } else if (numContribuinte === 1) {
      const currentVal = watch('rg_ie');
      if (currentVal?.toUpperCase() === 'ISENTO') {
        setValue('rg_ie', '');
      }
    }
  }, [contribuinteIcmsWatch, setValue, watch]);

  // Opções para tipo de cliente
  const getTipoClienteOptions = () => [
    { value: '01', label: '01 - Comercial' },
    { value: '02', label: '02 - Industrial' },
    { value: '03', label: '03 - Residencial/Pessoa Física' },
    { value: '04', label: '04 - Produtor Rural' },
    { value: '05', label: '05 - Órgão da administração pública estadual ICMS 107/95' },
    { value: '06', label: '06 - Prestador de serviço de telecomunicação ICMS 17/13' },
    { value: '07', label: '07 - Missões Diplomáticas ICMS 158/94' },
    { value: '08', label: '08 - Igrejas e Templos de qualquer natureza' },
    { value: '99', label: '99 - Outros não especificados anteriormente' }
  ];

  // Função para obter o primeiro escritório do usuário logado
  const getUserDefaultEscritorioId = (): number | undefined => {
    if (!user?.escritorio_ids) {
      // Se o usuário não tem escritórios específicos, retornar undefined
      // O componente EscritorioSelect vai mostrar todos os escritórios disponíveis
      return undefined;
    }

    // Se escritorio_ids é string JSON, fazer parse
    let escritorioIds: number[] = [];
    if (typeof user.escritorio_ids === 'string') {
      try {
        escritorioIds = JSON.parse(user.escritorio_ids);
      } catch (error) {
        console.error('Erro ao fazer parse do escritorio_ids:', error);
        return undefined;
      }
    } else if (Array.isArray(user.escritorio_ids)) {
      escritorioIds = user.escritorio_ids;
    } else {
      escritorioIds = [user.escritorio_ids];
    }

    // Retornar o primeiro escritório da lista
    return escritorioIds.length > 0 ? escritorioIds[0] : undefined;
  };

  // Função para verificar CPF/CNPJ duplicado
  const checkCpfCnpjDuplicate = async (cpfCnpj: string) => {
    if (!cpfCnpj || cpfCnpj.length < 14) {
      setError('CPF/CNPJ inválido');
      return;
    }

    try {
      setCpfCnpjChecking(true);
      setError(null);

      // Verificar se é CPF (14 dígitos) ou CNPJ (18 dígitos)
      if (cpfCnpj.length !== 14 && cpfCnpj.length !== 18) {
        return;
      }
      // Buscar cliente no banco de dados
      const existingClient = await clientService.getByCpfCnpj(cpfCnpj);
      if (existingClient) {
        // Se estiver editando o mesmo cliente, não mostrar erro
        if (editingClient && editingClient.id === existingClient.id) {
          return;
        }

        // Cliente já existe, mostrar alerta e redirecionar
        const documentType = cpfCnpj.length === 14 ? 'CPF' : 'CNPJ';
        setError(`${documentType} já cadastrado. Redirecionando para página do cliente...`);

        // Aguardar um pouco para o usuário ver a mensagem
        setTimeout(() => {
          navigate(`/clients/${existingClient.id}`);
        }, 2000);
      }
    } catch (err: any) {
      console.error('Erro ao verificar CPF/CNPJ:', err);
      // Não mostrar erro para o usuário, apenas log
    } finally {
      setCpfCnpjChecking(false);
    }
  };

  // Resetar formulário quando abrir/fechar
  useEffect(() => {
    if (open) {
      if (editingClient) {
        // Preencher formulário para edição
        const formData = {
          nome: editingClient.nome || '',
          cpf_cnpj: formatarCPFCNPJ(editingClient.cpf_cnpj || ''),
          tipo_pessoa: editingClient.tipo_pessoa || 'pf',
          data_nascimento: editingClient.data_nascimento || undefined,
          rg_ie: editingClient.rg_ie || '',
          contribuinte_icms: editingClient.contribuinte_icms || 0,
          nome_mae: editingClient.nome_mae || '',
          nome_pai: editingClient.nome_pai || '',
          endereco: editingClient.endereco || '',
          numero: editingClient.numero || '',
          bairro: editingClient.bairro || '',
          cidade: editingClient.cidade || '',
          codigo_municipio: editingClient.codigo_municipio || '',
          estado: editingClient.estado || '',
          cep: editingClient.cep || '',
          complemento: editingClient.complemento || '',
          referencia: editingClient.referencia || '',
          observacoes: editingClient.observacoes || '',
          tipo_cliente: editingClient.tipo_cliente || '',
          como_conheceu: editingClient.como_conheceu || '',
          tipo_moradia: editingClient.tipo_moradia ?? undefined,
          indicado_por: editingClient.indicado_por || undefined,
          vendedor_id: editingClient.vendedor_id || undefined,
          escritorio_id: editingClient.escritorio_id || undefined,
        };
        reset(formData);
      } else {
        // Obter o primeiro escritório e funcionário do usuário logado
        const defaultEscritorioId = getUserDefaultEscritorioId();
        const defaultVendedorId = user?.funcionario_id ?? undefined;
        // Resetar para novo cliente com escritório e vendedor padrão
        reset({
          nome: '',
          cpf_cnpj: '',
          tipo_pessoa: 'pf',
          data_nascimento: undefined,
          rg_ie: '',
          contribuinte_icms: undefined,
          nome_mae: '',
          nome_pai: '',
          endereco: '',
          numero: '',
          bairro: '',
          cidade: '',
          codigo_municipio: '',
          estado: '',
          cep: '',
          complemento: '',
          referencia: '',
          observacoes: '',
          tipo_cliente: '',
          como_conheceu: '',
          tipo_moradia: undefined,
          indicado_por: undefined,
          vendedor_id: defaultVendedorId, // Funcionário vinculado ao usuário logado
          escritorio_id: defaultEscritorioId, // Preenchido automaticamente com o primeiro escritório do usuário
        });
        setContacts([]);
      }
      setError(null);
    }
  }, [open, editingClient, reset, user]);

  // useEffect separado para definir escritório e vendedor padrão quando usuário for carregado
  useEffect(() => {
    if (open && !editingClient && user) {
      const defaultEscritorioId = getUserDefaultEscritorioId();
      const defaultVendedorId = user.funcionario_id;

      if (defaultEscritorioId) {
        setValue('escritorio_id', defaultEscritorioId);
      }
      if (defaultVendedorId) {
        setValue('vendedor_id', defaultVendedorId);
      }
    }
  }, [open, editingClient, user, setValue]);

  const onSubmit = async (data: Partial<Client>) => {
    try {
      setLoading(true);
      setError(null);
      // Limpar dados baseado no tipo de cliente
      const cleanedData = { ...data };

      // Para pessoa jurídica, limpar campos específicos de pessoa física
      if (cleanedData.tipo_pessoa === 'pj') {
        // Remover data_nascimento para pessoa jurídica
        cleanedData.data_nascimento = undefined;
      } else if (cleanedData.tipo_pessoa === 'pf') {
        // Para pessoa física, garantir que data_nascimento seja válida ou undefined
        if (cleanedData.data_nascimento && cleanedData.data_nascimento instanceof Date) {
          // Data já está como Date, manter assim
        } else if (cleanedData.data_nascimento === null || cleanedData.data_nascimento === '') {
          cleanedData.data_nascimento = undefined;
        }
      }

      // Para RG/IE, garantir que string vazia ou apenas espaços seja undefined (opcional para PF, obrigatório para PJ)
      if (cleanedData.rg_ie !== undefined && cleanedData.rg_ie !== null) {
        const rgIeTrimmed = String(cleanedData.rg_ie).trim();
        if (rgIeTrimmed === '') {
          cleanedData.rg_ie = undefined;
        } else {
          cleanedData.rg_ie = rgIeTrimmed;
        }
      } else if (cleanedData.rg_ie === '') {
        // Se for string vazia explícita, converter para undefined
        cleanedData.rg_ie = undefined;
      }

      // Limpar outros campos string vazios para undefined
      if (cleanedData.observacoes === '') {
        cleanedData.observacoes = undefined;
      }

      if (cleanedData.tipo_cliente === '') {
        cleanedData.tipo_cliente = undefined;
      }

      // Limpar indicado_por se não foi selecionado
      if (!cleanedData.indicado_por) {
        cleanedData.indicado_por = undefined;
      }

      if (!cleanedData.vendedor_id) {
        cleanedData.vendedor_id = undefined;
      }

      const tm = cleanedData.tipo_moradia;
      if (tm !== 'propria' && tm !== 'alugado') {
        cleanedData.tipo_moradia = undefined;
      }

      let client: Client;
      if (editingClient) {
        client = await clientService.update(editingClient.id, cleanedData);
      } else {
        const hasFixoOuCelular = contacts.some((c) => c.tipo === 'fixo' || c.tipo === 'celular');
        if (!hasFixoOuCelular) {
          setError('É obrigatório adicionar pelo menos 1 contato fixo ou celular');
          setLoading(false);
          return;
        }

        client = await clientService.create(cleanedData);
        for (const c of contacts) {
          const { tempId, ...contactData } = c;
          await contactService.create({
            nome: contactData.nome,
            cliente_id: client.id,
            tipo: contactData.tipo,
            email: contactData.email,
            numero: contactData.numero,
            is_whatsapp: contactData.is_whatsapp,
            is_default: contactData.is_default,
            recebe_notificacao: contactData.recebe_notificacao,
          } as any);
        }
      }

      onSuccess(client);
      onClose();
    } catch (err: any) {
      console.error('Erro ao salvar cliente:', err); // Log para depuração
      setError(err.response?.data?.message || 'Erro ao salvar cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (event?: any, reason?: string) => {
    if (reason === 'backdropClick') {
      return;
    }
    if (!loading) {
      onClose();
    }
  };

  const handleAddContact = async (data: Partial<import('../../types').Contact>) => {
    const pending: PendingContact = {
      tempId: `temp-${Date.now()}`,
      nome: data.nome,
      tipo: data.tipo!,
      email: data.email,
      numero: data.numero,
      is_whatsapp: data.is_whatsapp ?? false,
      is_default: data.is_default ?? false,
      recebe_notificacao: data.recebe_notificacao !== false,
    };

    setContacts((prev) => {
      const sameTypeCount = prev.filter((c) => c.tipo === pending.tipo).length;
      const shouldBeDefault = sameTypeCount === 0 || pending.is_default;

      const normalizedPending = {
        ...pending,
        is_default: shouldBeDefault,
      };

      return [
        ...prev.map((c) =>
          c.tipo === normalizedPending.tipo && normalizedPending.is_default
            ? { ...c, is_default: false }
            : c
        ),
        normalizedPending,
      ];
    });
    setContactDialogOpen(false);
  };

  const handleRemoveContact = (tempId: string) => {
    setContacts((prev) => prev.filter((c) => c.tempId !== tempId));
  };

  const getContactIcon = (tipo: string) => {
    switch (tipo) {
      case 'email': return <Email color="primary" sx={{ fontSize: '1rem' }} />;
      case 'fixo': return <Phone color="primary" sx={{ fontSize: '1rem' }} />;
      case 'celular': return <ContactPhone color="primary" sx={{ fontSize: '1rem' }} />;
      default: return <ContactPhone color="primary" sx={{ fontSize: '1rem' }} />;
    }
  };

  const getContactValue = (c: PendingContact) => {
    if (c.tipo === 'email') return c.email || '';
    return c.numero ? formatarPhone(c.numero) : '';
  };

  const formatContactType = (tipo: string) => {
    const map: Record<string, string> = { email: 'Email', fixo: 'Fixo', celular: 'Celular' };
    return map[tipo] || tipo;
  };

  // Função para buscar CEP usando a função utilitária
  const handleBuscarCep = async () => {
    await buscarCep(cepWatch || '', setValue, setError, setCepLoading);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(0, 0, 0, 0.4)'
        }
      }}
    >
      <DialogTitle sx={{ p: 1.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Person color="primary" />
            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
              {editingClient ? `Editar Cliente - ${editingClient.nome}` : 'Novo Cliente'}
            </Typography>
          </Box>
          <Button
            onClick={handleClose}
            disabled={loading}
            color="inherit"
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            <Close />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1.5 }} ref={dialogContentRef}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
            {error}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={12}>
              <Controller
                name="escritorio_id"
                control={control}
                render={({ field }) => (
                  <EscritorioSelect
                    value={field.value || null}
                    onChange={field.onChange}
                    label="Escritório"
                    size="small"
                    placeholder="Selecione o escritório"
                    error={!!errors.escritorio_id}
                    helperText={errors.escritorio_id?.message || "Escritório responsável pelo cliente"}
                    onlyActive={true}
                    allowClear={false}
                    multiple={false}
                    required={true}
                  />
                )}
              />
            </Grid>
            {/* Dados Pessoais */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', mb: 1 }}>
                <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                Dados Pessoais
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="tipo_pessoa"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.tipo_pessoa}>
                    <InputLabel>Tipo Pessoa</InputLabel>
                    <Select {...field} label="Tipo Pessoa" size="small">
                      <MenuItem value="pf">Pessoa Física</MenuItem>
                      <MenuItem value="pj">Pessoa Jurídica</MenuItem>
                    </Select>
                    {errors.tipo_pessoa && (
                      <FormHelperText>{errors.tipo_pessoa.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <Controller
                name="nome"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label={tipoClienteWatch === 'pj' ? 'Razão Social' : 'Nome Completo'}
                    error={!!errors.nome || hasTrailingSpaceInNome}
                    helperText={
                      errors.nome?.message ||
                      (hasTrailingSpaceInNome ? 'Remova o(s) espaço(s) no final do nome' : undefined)
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            {hasTrailingSpaceInNome && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ fontSize: '0.8rem', py: 0.5 }}>
                  O nome possui espaço(s) no final. Corrija antes de salvar.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <Controller
                name="cpf_cnpj"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label={tipoClienteWatch === 'pf' ? 'CPF' : 'CNPJ'}
                    error={!!errors.cpf_cnpj}
                    helperText={errors.cpf_cnpj?.message}
                    onChange={(e) => {
                      const formatted = formatarCPFCNPJOnInput(e.target.value);
                      field.onChange(formatted);
                    }}
                    onBlur={async () => {
                      await checkCpfCnpjDuplicate(field.value || '');
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Assignment />
                        </InputAdornment>
                      ),
                      endAdornment: cpfCnpjChecking && (
                        <InputAdornment position="end">
                          <CircularProgress size={16} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="rg_ie"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label={tipoClienteWatch === 'pf' ? 'RG' : 'IE'}
                    error={!!errors.rg_ie}
                    helperText={errors.rg_ie?.message || (tipoClienteWatch === 'pj' ? 'Apenas números ou isento' : 'Registro Geral (opcional para PF)')}
                    required={tipoClienteWatch === 'pj' || Number(contribuinteIcmsWatch) === 1}
                    disabled={Number(contribuinteIcmsWatch) === 2}
                  />
                )}
              />
            </Grid>

            {tipoClienteWatch === 'pf' && (
              <>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="data_nascimento"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Data de Nascimento"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: "1900-01-01", max: "2999-12-31" }}
                        error={!!errors.data_nascimento}
                        helperText={errors.data_nascimento?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="nome_mae"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Nome da Mãe"
                        error={!!errors.nome_mae}
                        helperText={errors.nome_mae?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="nome_pai"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Nome do Pai"
                        error={!!errors.nome_pai}
                        helperText={errors.nome_pai?.message}
                      />
                    )}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={6}>
              <Controller
                name="como_conheceu"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.como_conheceu} required>
                    <InputLabel>Como conheceu a Empresa?</InputLabel>
                    <Select {...field} label="Como conheceu a Empresa?" size="small">
                      {COMO_CONHECEU_OPTIONS.map((opcao) => (
                        <MenuItem key={opcao} value={opcao}>
                          {opcao}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.como_conheceu && (
                      <FormHelperText>{errors.como_conheceu.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="indicado_por"
                control={control}
                render={({ field }) => (
                  <ClientSelect
                    value={field.value || null}
                    onChange={(clientId) => field.onChange(clientId)}
                    label="Indicado por"
                    size="small"
                    placeholder="Busque o cliente que fez a indicação"
                    error={!!errors.indicado_por}
                    helperText={errors.indicado_por?.message || (comoConheceuWatch === 'Indicação de amigo' ? "Selecione o cliente que indicou (obrigatório)" : "Disponível apenas para 'Indicação de amigo'")}
                    onlyActive={true}
                    allowClear={true}
                    disabled={!!editingClient || comoConheceuWatch !== 'Indicação de amigo'}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={12}>
              <Controller
                name="vendedor_id"
                control={control}
                render={({ field }) => (
                  <FuncionariosSelect
                    value={field.value ?? null}
                    onChange={(id) => field.onChange(id)}
                    label="Vendedor"
                    size="small"
                    placeholder="Selecione o vendedor"
                    error={!!errors.vendedor_id}
                    helperText={errors.vendedor_id?.message || "Funcionário vendedor responsável (opcional)"}
                    allowClear={true}
                    disabled={!!editingClient}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="tipo_cliente"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.tipo_cliente}>
                    <InputLabel>Tipo de Cliente</InputLabel>
                    <Select {...field} label="Tipo de Cliente" size="small">
                      {getTipoClienteOptions().map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.tipo_cliente && (
                      <FormHelperText>{errors.tipo_cliente.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="contribuinte_icms"
                control={control}
                rules={{
                  required: {
                    value: tipoClienteWatch === 'pj',
                    message: 'Contribuinte ICMS é obrigatório para pessoa jurídica'
                  }
                }}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.contribuinte_icms}>
                    <InputLabel>Contribuinte ICMS</InputLabel>
                    <Select {...field} label="Contribuinte ICMS" size="small">
                      <MenuItem value="1">1 - Contribuinte ICMS</MenuItem>
                      <MenuItem value="2">2 - Contribuinte isento de Inscrição no cadastro de Contribuintes do ICMS</MenuItem>
                      <MenuItem value="9">9 - Não contribuinte ICMS</MenuItem>
                    </Select>
                    {errors.contribuinte_icms && (
                      <FormHelperText>{errors.contribuinte_icms.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Contatos - somente ao cadastrar novo cliente */}
            {!editingClient && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', mb: 0 }}>
                    <ContactPhone sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Contatos
                    <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      Obrigatório pelo menos 1 contato fixo ou celular
                    </Typography>
                  </Typography>
                </Box>
                <Divider sx={{ mb: 1.5 }} />


                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', width: 40 }}></TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>Tipo</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>Contato</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>WhatsApp</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>Padrão</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>Notificações</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', width: 60 }}>Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        onClick={() => setContactDialogOpen(true)}
                      >
                        <TableCell colSpan={7} sx={{ py: 0.75, px: 1 }}>
                          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1} color="primary.main">
                            <Add fontSize="small" />
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              Adicionar contato
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                      {contacts.map((c) => (
                        <TableRow key={c.tempId} hover>
                          <TableCell sx={{ py: 0.5, px: 1, width: 40 }}></TableCell>
                          <TableCell sx={{ py: 0.5, px: 1 }}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {getContactIcon(c.tipo)}
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {formatContactType(c.tipo)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.8rem' }}>
                            {getContactValue(c)}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                            {c.tipo === 'celular' && c.is_whatsapp ? (
                              <Chip label="Sim" color="success" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                            ) : (
                              <Chip label="Não" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                            {c.is_default ? <StarIcon color="warning" sx={{ fontSize: '1rem' }} /> : <StarBorderIcon color="disabled" sx={{ fontSize: '1rem' }} />}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                            {c.recebe_notificacao ? (
                              <Chip icon={<Notifications sx={{ fontSize: '0.75rem' }} />} label="Sim" color="primary" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                            ) : (
                              <Chip label="Não" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                            <IconButton size="small" onClick={() => handleRemoveContact(c.tempId)} color="error" title="Remover" sx={{ p: 0.25 }}>
                              <Delete sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {/* Endereço */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', mb: 1 }}>
                <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
                Endereço
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
            </Grid>

            <Grid item xs={12} md={2.5}>
              <Controller
                name="cep"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="CEP"
                    error={!!errors.cep}
                    helperText={errors.cep?.message}
                    onChange={(e) => {
                      const formatted = formatarCepOnInput(e.target.value);
                      field.onChange(formatted);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleBuscarCep();
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Buscar CEP">
                            <IconButton
                              onClick={handleBuscarCep}
                              disabled={cepLoading || !cepWatch || cepWatch.length < 8}
                              size="small"
                              sx={{ p: 0.5 }}
                            >
                              {cepLoading ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Search sx={{ fontSize: '1rem' }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Controller
                name="cidade"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Cidade"
                    error={!!errors.cidade}
                    helperText={errors.cidade?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <Controller
                name="estado"
                control={control}
                render={({ field }) => (
                  <EstadosBrSelect
                    value={field.value || ''}
                    onChange={field.onChange}
                    label="Estado"
                    error={!!errors.estado}
                    helperText={errors.estado?.message}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Controller
                name="codigo_municipio"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Cód. IBGE"
                    error={!!errors.codigo_municipio}
                    helperText={errors.codigo_municipio?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="bairro"
                control={control}
                render={({ field }) => (
                  <BairroSelect
                    value={field.value || null}
                    onChange={(v) => field.onChange(v || '')}
                    label="Bairro"
                    size="small"
                    allowCustom
                    allowClear={false}
                    required
                    error={!!errors.bairro}
                    helperText={errors.bairro?.message}
                    disabled={!cidadeWatch?.trim()}
                    cidade_inst={cidadeWatch}
                    estado_inst={estadoWatch}
                    escritorio_id={watch('escritorio_id')}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="endereco"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Endereço"
                    error={!!errors.endereco}
                    helperText={errors.endereco?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Controller
                name="numero"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Número"
                    error={!!errors.numero}
                    helperText={errors.numero?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="complemento"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Complemento"
                    inputProps={{ autoComplete: 'off' }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Controller
                name="referencia"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Referência"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Controller
                name="tipo_moradia"
                control={control}
                render={({ field }) => (
                  <FormControl component="fieldset" size="small" error={!!errors.tipo_moradia}>
                    <RadioGroup row {...field} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || undefined)}>
                      <FormControlLabel value="propria" control={<Radio size="small" />} label="Própria" />
                      <FormControlLabel value="alugado" control={<Radio size="small" />} label="Alugado" />
                    </RadioGroup>
                    {errors.tipo_moradia && (
                      <FormHelperText>{errors.tipo_moradia.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="observacoes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Observações"
                    multiline
                    rows={2}
                    error={!!errors.observacoes}
                    helperText={errors.observacoes?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      {!editingClient && (
        <ContactForm
          open={contactDialogOpen}
          onClose={() => setContactDialogOpen(false)}
          onSubmit={handleAddContact}
          editingContact={null}
          clientId={0}
          loading={false}
        />
      )}

      <DialogActions sx={{ p: 1.5 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
          size="small"
          sx={{ fontSize: '0.8rem' }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={loading || hasTrailingSpaceInNome}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <Save />}
          size="small"
          sx={{ fontSize: '0.8rem' }}
        >
          {loading ? 'Salvando...' : editingClient ? 'Atualizar' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientForm; 